import { type Registry as Services, service } from '@ember/service';
import { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import type { ParityManagerIssuesummary } from 'lumen/models';
import ParityManagerStat from 'lumen/models/parity-manager/stat';

export const ESCALATION_LEVELS = {
  HEALTHY: -1,
  WARNING: 0,
  LEVEL_ONE: 1,
  LEVEL_TWO: 2,
  LEVEL_THREE: 3,
} as const;

type EscalationLevel =
  (typeof ESCALATION_LEVELS)[keyof typeof ESCALATION_LEVELS];

export const BADGE_TYPES = {
  NEUTRAL: 'neutral',
  WARNING: 'warning',
  DANGER: 'danger',
} as const;

export const ESCALATION_LEVEL_TRANSLATIONS = {
  [ESCALATION_LEVELS.HEALTHY]: tKey('parity_manager.escalation.healthy'),
  [ESCALATION_LEVELS.WARNING]: tKey('parity_manager.escalation.warning'),
  [ESCALATION_LEVELS.LEVEL_ONE]: tKey('parity_manager.escalation.level'),
  [ESCALATION_LEVELS.LEVEL_TWO]: tKey('parity_manager.escalation.level'),
  [ESCALATION_LEVELS.LEVEL_THREE]: tKey('parity_manager.escalation.level'),
} as const;

export const ESCALATION_LEVEL_BADGE_TYPES = {
  [ESCALATION_LEVELS.HEALTHY]: BADGE_TYPES.NEUTRAL,
  [ESCALATION_LEVELS.WARNING]: BADGE_TYPES.NEUTRAL,
  [ESCALATION_LEVELS.LEVEL_ONE]: BADGE_TYPES.NEUTRAL,
  [ESCALATION_LEVELS.LEVEL_TWO]: BADGE_TYPES.WARNING,
  [ESCALATION_LEVELS.LEVEL_THREE]: BADGE_TYPES.DANGER,
} as const;

export default class ParityManagerEscalationinfo extends ParityManagerStat {
  // @ts-expect-error: Class inheritance
  [Type] = 'parity-manager/escalationinfo' as const;

  @belongsTo('parity-manager/issuesummary', {
    async: false,
    inverse: 'escalationinfo',
  })
  declare subscriptionId: ParityManagerIssuesummary;

  @attr('number') declare escalationLevel: EscalationLevel;
  @attr('string') declare firstConfirmedDate: string;
  @attr('string') declare firstViolationDate: string;
  @attr('string') declare lastConfirmedDate: string;
  @attr('string') declare lastViolationDate: string;
  @attr('number') declare numConfirmed: number;
  @attr('number') declare numDaysWithConfirmed: number;
  @attr('number') declare numWaived: number;

  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get escalationBadgeType() {
    return ESCALATION_LEVEL_BADGE_TYPES[this.escalationLevelWithFallback];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get escalationLevelWithFallback() {
    return this.escalationLevel ?? ESCALATION_LEVELS.HEALTHY;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedEscalationLevel() {
    return this.intl.t(
      ESCALATION_LEVEL_TRANSLATIONS[this.escalationLevelWithFallback],
      {
        escalationLevel: this.escalationLevel,
      },
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedFirstConfirmedDate() {
    if (this.firstConfirmedDate) {
      return dayjs.utc(this.firstConfirmedDate).format('L');
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedFirstLevelOneIssueDate() {
    if (this.firstViolationDate) {
      return dayjs.utc(this.firstViolationDate).format('L');
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedLastConfirmedDate() {
    if (this.lastConfirmedDate) {
      const formattedLastConfirmedDate = dayjs
        .utc(this.lastConfirmedDate)
        .format('L');

      // Only show the lastConfirmedDate if it is different from the firstViolationDate or the firstConfirmedDate
      if (this.firstViolationDate) {
        if (
          formattedLastConfirmedDate !== this.formattedFirstLevelOneIssueDate
        ) {
          return formattedLastConfirmedDate;
        }
      } else if (
        formattedLastConfirmedDate !== this.formattedFirstConfirmedDate
      ) {
        return formattedLastConfirmedDate;
      }
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasEscalationLevelWarningOrAbove() {
    return this.escalationLevelWithFallback >= ESCALATION_LEVELS.WARNING;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasMultipleDaysWithViolations() {
    return this.numDaysWithConfirmed > 1;
  }
}
